import { database, isFirebaseReady } from './config';
import { ref, onValue, off, set, get } from 'firebase/database';

export const SAT_SEMESTERS = ['Fall 2023', 'Spring 2024', 'Fall 2024', 'Spring 2025', 'Fall 2025'];

const SCORECARD_TEAMS = ['Team A', 'Team B', 'Team C', 'Team D'];
const SAT_HISTORY_PATH = 'sat_history';

const getSemesterKey = (semester) => semester.toLowerCase().replace(/\s+/g, '-');

const normaliseFinalScorecard = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return SCORECARD_TEAMS.map((team) => ({
      teamPlayers: `${team} Roster TBD`,
      score: 'TBD'
    }));
  }

  return rows.map((row, index) => ({
    teamPlayers: row?.teamPlayers || `${SCORECARD_TEAMS[index] || `Team ${index + 1}`} Roster TBD`,
    score: row?.score || 'TBD'
  }));
};

const normalisePodium = (podium, label) => ({
  label,
  name: typeof podium?.name === 'string' && podium.name.trim() ? podium.name : 'Team TBD'
});

export const createDefaultSemesterData = () => ({
  champions: normalisePodium({}, 'Champions'),
  runnersUp: normalisePodium({}, 'Runner-Up'),
  finalScorecard: SCORECARD_TEAMS.map((team) => ({
    teamPlayers: `${team} Roster TBD`,
    score: 'TBD'
  }))
});

export const createDefaultSatHistory = () => {
  const defaults = {};
  SAT_SEMESTERS.forEach((semester) => {
    defaults[semester] = createDefaultSemesterData();
  });
  return defaults;
};

const normaliseEntry = (entry = {}, semester) => ({
  ...createDefaultSemesterData(),
  champions: normalisePodium(entry?.champions, 'Champions'),
  runnersUp: normalisePodium(entry?.runnersUp, 'Runner-Up'),
  finalScorecard: normaliseFinalScorecard(entry?.finalScorecard),
  semester
});

const serialiseEntry = (entry, semester) => ({
  semester,
  champions: { name: entry?.champions?.name || 'Team TBD' },
  runnersUp: { name: entry?.runnersUp?.name || 'Team TBD' },
  finalScorecard: Array.isArray(entry?.finalScorecard)
    ? entry.finalScorecard.map((row) => ({
        teamPlayers: row?.teamPlayers || '',
        score: row?.score || ''
      }))
    : createDefaultSemesterData().finalScorecard
});

export const subscribeToSatHistory = (callback) => {
  const emitDefaults = () => callback(createDefaultSatHistory());

  if (!isFirebaseReady || !database) {
    console.warn('Firebase not ready. Serving default SAT history data.');
    emitDefaults();
    return () => {};
  }

  const satRef = ref(database, SAT_HISTORY_PATH);

  const handler = (snapshot) => {
    const firebaseData = snapshot.exists() ? snapshot.val() : {};
    const merged = createDefaultSatHistory();

    SAT_SEMESTERS.forEach((semester) => {
      const key = getSemesterKey(semester);
      if (firebaseData[key]) {
        merged[semester] = normaliseEntry(firebaseData[key], semester);
      }
    });

    callback(merged);
  };

  const errorHandler = (error) => {
    console.error('Error subscribing to SAT history:', error);
    emitDefaults();
  };

  onValue(satRef, handler, errorHandler);

  return () => {
    off(satRef, 'value', handler);
  };
};

export const updateSatPodium = async (semester, updates) => {
  if (!semester) {
    throw new Error('Semester is required to update SAT podium data.');
  }

  if (!isFirebaseReady || !database) {
    throw new Error('Firebase is not ready. Cannot persist SAT updates.');
  }

  const key = getSemesterKey(semester);
  const satRef = ref(database, `${SAT_HISTORY_PATH}/${key}`);

  const snapshot = await get(satRef);
  const existingEntry = snapshot.exists() ? snapshot.val() : {};
  const normalisedExisting = normaliseEntry(existingEntry, semester);

  const mergedEntry = {
    ...normalisedExisting,
    champions: updates?.champions
      ? normalisePodium(
          {
            ...normalisedExisting.champions,
            ...updates.champions
          },
          'Champions'
        )
      : normalisedExisting.champions,
    runnersUp: updates?.runnersUp
      ? normalisePodium(
          {
            ...normalisedExisting.runnersUp,
            ...updates.runnersUp
          },
          'Runner-Up'
        )
      : normalisedExisting.runnersUp,
    finalScorecard: updates?.finalScorecard
      ? normaliseFinalScorecard(updates.finalScorecard)
      : normalisedExisting.finalScorecard
  };

  await set(satRef, serialiseEntry(mergedEntry, semester));

  return mergedEntry;
};
